<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import api from '@/services/api'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { agoraUtcISO, formatarTimestampBR } from '@/utils/useDataBR'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import AbasFormulario from '@/components/base/AbasFormulario.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import CampoTimeStamp from '@/components/base/campos/CampoTimeStamp.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { TAB_PRECO_TP_PRECIFIC } from '@/constants/TAB_PRECO_TP_PRECIFIC'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'

// ─── Props e Emits ───────────────────────────────────────────
const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idPrecoComercial: 0,
      nome: '',
      lstTipoPrecificacao: '',
      valorBasePrecificacao: null,
      lstMoeda: '',
      lstStatus: '',
      idOrganizacao: null
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

// ─── Stores ──────────────────────────────────────────────────
const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

// ─── useFormulario (cabeçalho) ───────────────────────────────
const { item, isLoading, isEditing, entityInfo, submitForm, handleCancel } =
  useFormulario(props, emit)

// ─── Permissões ──────────────────────────────────────────────
const temPermissaoCompleta = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('TABELA_PRECOS_COMPLETO')) return true
  if (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')) return true
  return authStore.getPermsDoModulo('VENDAS').some(p =>
    p === 'COMPLETO' || (p.includes('TABELA_PRECOS') && p.includes('COMPLETO'))
  )
})
const isReadOnly = computed(() => isEditing.value && !temPermissaoCompleta.value)

// ─── Salvar e Continuar ──────────────────────────────────────
const isHeaderSaved = ref(isEditing.value)
const isSavingHeaderLocal = ref(false)
// As abas ficam bloqueadas na criação até o cabeçalho ser salvo com sucesso
// (botão "Salvar Cabeçalho e Liberar Abas") ou em modo leitura.
const abasBloqueadas = computed(() => isReadOnly.value || !isHeaderSaved.value)

// ─── Abas ────────────────────────────────────────────────────
const abas = [
  { key: 'especificacoes', label: 'Especificações Produtos', icon: 'bi-box-seam' },
  { key: 'campanhas', label: 'Campanhas', icon: 'bi-megaphone' }
]
const abaAtiva = ref('especificacoes')

// ─── Listas auxiliares ───────────────────────────────────────
const listas = reactive({})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['PrecoComercial'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

// ─── Opções fixas ────────────────────────────────────────────
const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

// ─── Helpers de exibição ─────────────────────────────────────
const labelTipoPrecificacao = (codigo) => {
  if (codigo === TAB_PRECO_TP_PRECIFIC.PrecoFixo) return 'Preço Fixo'
  if (codigo === TAB_PRECO_TP_PRECIFIC.MargemBruta) return 'Margem Bruta'
  return codigo || '-'
}

const tipoValorInput = (tipo) => {
  return tipo === TAB_PRECO_TP_PRECIFIC.MargemBruta ? 'percentual' : 'moeda'
}

const tipoValorLabel = (tipo) => {
  if (tipo === TAB_PRECO_TP_PRECIFIC.MargemBruta) return 'Margem (%)'
  if (tipo === TAB_PRECO_TP_PRECIFIC.PrecoFixo) return 'Preço Fixo'
  return 'Valor Base'
}

const getMoedaIso = (idMoeda) => {
  if (!idMoeda) return 'BRL'
  const moedaObj = (listas.LstMoeda || []).find(m => m.idListaClienteItem === idMoeda || m.textoLista === idMoeda)
  const texto = moedaObj ? moedaObj.textoLista : idMoeda
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : 'BRL'
}

const formatarValorBase = (valor, tipo, moeda) => {
  if (valor == null) return '-'
  if (tipo === TAB_PRECO_TP_PRECIFIC.MargemBruta) {
    return `${Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
  }
  const isoCode = getMoedaIso(moeda)
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: isoCode }).format(valor)
  } catch (e) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)
  }
}

// ─── Auditoria ───────────────────────────────────────────────
const mostrarAuditoria = ref(false)

// ─── Salvar cabeçalho ────────────────────────────────────────
// Retorna true se salvou com sucesso (usado para liberar o cadastro de filhos).
const salvarCabecalhoLocalmente = async () => {
  if (!temPermissaoCompleta.value || isSavingHeaderLocal.value) return false
  if (!item.value.nome || !item.value.lstTipoPrecificacao || item.value.valorBasePrecificacao == null ||
      !item.value.lstMoeda || !item.value.lstStatus) {
    emit('show-notification', { message: 'Preencha todos os campos obrigatórios do cabeçalho.', type: 'warning' })
    return false
  }

  isSavingHeaderLocal.value = true
  try {
    const payload = { ...item.value }
    const response = await api.post('/PrecosComerciais', payload)
    // Atualiza o estado local sem emitir itemAtualizado (não fecha o painel).
    item.value = response.data
    isHeaderSaved.value = true
    emit('show-notification', { message: 'Cabeçalho salvo. Agora você pode gerenciar campanhas e especificações.', type: 'success' })
    return true
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar o cabeçalho.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
    return false
  } finally {
    isSavingHeaderLocal.value = false
  }
}

const submitFormDoCabecalho = async () => {
  if (!temPermissaoCompleta.value) return
  const payload = { ...item.value }
  const success = await submitForm('PrecosComerciais', 'idPrecoComercial', payload)
  if (success) {
    isHeaderSaved.value = true
    emit('show-notification', { message: 'Tabela de preços salva com sucesso!', type: 'success' })
  }
}

// Salva o cabeçalho sem fechar o formulário (permanece para gerenciar filhos).
const salvarAlteracoesCabecalho = async () => {
  if (!temPermissaoCompleta.value || isLoading.value) return
  if (!item.value.idPrecoComercial) {
    await salvarCabecalhoLocalmente()
    return
  }
  isLoading.value = true
  try {
    const payload = { ...item.value }
    const response = await api.put(`/PrecosComerciais/${item.value.idPrecoComercial}`, payload)
    // Atualiza o estado local sem emitir itemAtualizado (não fecha o painel).
    item.value = response.data
    emit('show-notification', { message: 'Alterações do cabeçalho salvas com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar as alterações do cabeçalho.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

// ─── Aba: Especificações Produtos ────────────────────────────
const especificacoes = ref([])
const isLoadingEspec = ref(false)
const filtrosEspec = reactive({
  tsReferencia: agoraUtcISO(),
  ignorarData: false,
  idProduto: null
})

const buscarEspecificacoes = async () => {
  if (!item.value.idPrecoComercial) {
    especificacoes.value = []
    return
  }
  isLoadingEspec.value = true
  try {
    const params = new URLSearchParams()
    params.append('idPrecoComercial', item.value.idPrecoComercial)
    params.append('pageSize', 999)
    params.append('sortField', 'tsInicioVigencia')
    params.append('sortOrder', 'desc')
    if (filtrosEspec.ignorarData) {
      params.append('ignorarData', 'true')
    } else if (filtrosEspec.tsReferencia) {
      params.append('tsReferencia', filtrosEspec.tsReferencia)
    }
    if (filtrosEspec.idProduto) params.append('idProduto', filtrosEspec.idProduto)

    const response = await api.get(`/PrecosComerciaisProdutos?${params.toString()}`)
    especificacoes.value = response.data
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar especificações de produtos.', type: 'error' })
    especificacoes.value = []
  } finally {
    isLoadingEspec.value = false
  }
}

const limparFiltrosEspec = () => {
  filtrosEspec.tsReferencia = agoraUtcISO()
  filtrosEspec.ignorarData = false
  filtrosEspec.idProduto = null
  buscarEspecificacoes()
}

// ─── Aba: Campanhas ──────────────────────────────────────────
const campanhas = ref([])
const isLoadingCampanhas = ref(false)
const campanhaSelecionada = ref(null)
const especificacoesCampanha = ref([])
const isLoadingEspecCampanha = ref(false)
const filtroProdutoCampanha = ref(null)

const buscarCampanhas = async () => {
  if (!item.value.idPrecoComercial) {
    campanhas.value = []
    return
  }
  isLoadingCampanhas.value = true
  try {
    const params = new URLSearchParams()
    params.append('idPrecoComercial', item.value.idPrecoComercial)
    params.append('pageSize', 999)
    params.append('sortField', 'tsInicioVigencia')
    params.append('sortOrder', 'desc')

    const response = await api.get(`/PrecosComerciaisCampanhas?${params.toString()}`)
    campanhas.value = response.data
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar campanhas.', type: 'error' })
    campanhas.value = []
  } finally {
    isLoadingCampanhas.value = false
  }
}

const selecionarCampanha = (campanha) => {
  campanhaSelecionada.value = campanha
  filtroProdutoCampanha.value = null
  buscarEspecificacoesCampanha()
}

const buscarEspecificacoesCampanha = async () => {
  if (!campanhaSelecionada.value) {
    especificacoesCampanha.value = []
    return
  }
  isLoadingEspecCampanha.value = true
  try {
    const params = new URLSearchParams()
    params.append('idPrecoComercial', item.value.idPrecoComercial)
    params.append('idPrecoComercialCampanha', campanhaSelecionada.value.idPrecoComercialCampanha)
    params.append('ignorarData', 'true')
    params.append('pageSize', 999)
    if (filtroProdutoCampanha.value) params.append('idProduto', filtroProdutoCampanha.value)

    const response = await api.get(`/PrecosComerciaisProdutos?${params.toString()}`)
    especificacoesCampanha.value = response.data
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar especificações da campanha.', type: 'error' })
    especificacoesCampanha.value = []
  } finally {
    isLoadingEspecCampanha.value = false
  }
}

// ─── Modal: Campanha ─────────────────────────────────────────
const campanhaEmEdicao = ref(null)
const isSavingCampanha = ref(false)

const showModalCampanha = computed({
  get: () => !!campanhaEmEdicao.value,
  set: (val) => { if (!val) campanhaEmEdicao.value = null }
})

// Regra de imutabilidade: ao editar uma campanha existente, apenas a vigência é editável.
const isEditandoCampanha = computed(() => !!(campanhaEmEdicao.value && campanhaEmEdicao.value.idPrecoComercialCampanha))

const abrirModalCampanha = async (campanha = null) => {
  // Bloqueado enquanto o cabeçalho não for salvo com sucesso.
  if (isReadOnly.value || !isHeaderSaved.value) return
  if (campanha) {
    campanhaEmEdicao.value = { ...campanha }
  } else {
    campanhaEmEdicao.value = {
      idPrecoComercialCampanha: 0,
      idPrecoComercial: item.value.idPrecoComercial,
      descricao: '',
      lstTipoPrecificacao: '',
      valorBasePrecificacao: null,
      tsInicioVigencia: agoraUtcISO(),
      tsFimVigencia: null
    }
  }
}

const salvarCampanha = async () => {
  const c = campanhaEmEdicao.value
  if (!c) return
  if (!c.descricao || !c.lstTipoPrecificacao || c.valorBasePrecificacao == null ||
      !c.tsInicioVigencia || !c.tsFimVigencia) {
    emit('show-notification', { message: 'Preencha todos os campos obrigatórios da campanha.', type: 'warning' })
    return
  }

  isSavingCampanha.value = true
  try {
    const payload = {
      idPrecoComercial: item.value.idPrecoComercial,
      descricao: c.descricao,
      lstTipoPrecificacao: c.lstTipoPrecificacao,
      valorBasePrecificacao: c.valorBasePrecificacao,
      tsInicioVigencia: c.tsInicioVigencia,
      tsFimVigencia: c.tsFimVigencia,
      xmin: c.xmin || null
    }

    if (c.idPrecoComercialCampanha) {
      await api.put(`/PrecosComerciaisCampanhas/${c.idPrecoComercialCampanha}`, payload)
    } else {
      await api.post('/PrecosComerciaisCampanhas', payload)
    }

    emit('show-notification', { message: 'Campanha salva com sucesso!', type: 'success' })
    campanhaEmEdicao.value = null
    await buscarCampanhas()
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar a campanha.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSavingCampanha.value = false
  }
}

const excluirCampanha = async (campanha) => {
  if (isReadOnly.value) return
  try {
    await api.delete(`/PrecosComerciaisCampanhas/${campanha.idPrecoComercialCampanha}`)
    emit('show-notification', { message: 'Campanha excluída com sucesso!', type: 'success' })
    if (campanhaSelecionada.value?.idPrecoComercialCampanha === campanha.idPrecoComercialCampanha) {
      campanhaSelecionada.value = null
      especificacoesCampanha.value = []
    }
    await buscarCampanhas()
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao excluir a campanha.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// ─── Modal: Especificação ────────────────────────────────────
const especificacaoEmEdicao = ref(null)
const isSavingEspec = ref(false)

const showModalEspecificacao = computed({
  get: () => !!especificacaoEmEdicao.value,
  set: (val) => { if (!val) especificacaoEmEdicao.value = null }
})

const abrirModalEspecificacao = async (espec = null, idCampanha = null) => {
  // Bloqueado enquanto o cabeçalho não for salvo com sucesso.
  if (isReadOnly.value || !isHeaderSaved.value) return
  if (espec) {
    especificacaoEmEdicao.value = { ...espec }
  } else {
    especificacaoEmEdicao.value = {
      idPrecoComercialProduto: 0,
      idPrecoComercial: item.value.idPrecoComercial,
      idPrecoComercialCampanha: idCampanha,
      idProduto: null,
      descricao: '',
      lstTipoPrecificacao: '',
      valorBasePrecificacao: null,
      tsInicioVigencia: agoraUtcISO(),
      tsFimVigencia: null
    }
  }
}

const salvarEspecificacao = async () => {
  const e = especificacaoEmEdicao.value
  if (!e) return
  if (!e.idProduto || !e.lstTipoPrecificacao || e.valorBasePrecificacao == null) {
    emit('show-notification', { message: 'Preencha todos os campos obrigatórios da especificação.', type: 'warning' })
    return
  }

  isSavingEspec.value = true
  try {
    const payload = {
      idPrecoComercial: item.value.idPrecoComercial,
      idPrecoComercialCampanha: e.idPrecoComercialCampanha || null,
      idProduto: e.idProduto,
      descricao: e.descricao || null,
      lstTipoPrecificacao: e.lstTipoPrecificacao,
      valorBasePrecificacao: e.valorBasePrecificacao,
      tsInicioVigencia: e.tsInicioVigencia || null,
      tsFimVigencia: e.tsFimVigencia || null,
      xmin: e.xmin || null
    }

    if (e.idPrecoComercialProduto) {
      await api.put(`/PrecosComerciaisProdutos/${e.idPrecoComercialProduto}`, payload)
    } else {
      await api.post('/PrecosComerciaisProdutos', payload)
    }

    emit('show-notification', { message: 'Especificação salva com sucesso!', type: 'success' })
    especificacaoEmEdicao.value = null

    if (e.idPrecoComercialCampanha) {
      await buscarEspecificacoesCampanha()
    } else {
      await buscarEspecificacoes()
    }
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar a especificação.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSavingEspec.value = false
  }
}

const excluirEspecificacao = async (espec, origemCampanha = false) => {
  if (isReadOnly.value) return
  try {
    await api.delete(`/PrecosComerciaisProdutos/${espec.idPrecoComercialProduto}`)
    emit('show-notification', { message: 'Especificação excluída com sucesso!', type: 'success' })
    if (origemCampanha) {
      await buscarEspecificacoesCampanha()
    } else {
      await buscarEspecificacoes()
    }
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao excluir a especificação.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// ─── Config de busca de produto ──────────────────────────────
// Filtro fixo e implícito: apenas produtos cujo TipoProduto.Comercializavel = true
// podem ter preço definido (especificações de produto e de campanha).
const configBuscaProduto = computed(() => {
  const base = criarConfigBuscaProduto()
  return {
    ...base,
    filtrosFixos: {
      ...base.filtrosFixos,
      comercializavel: true
    }
  }
})

// ─── Ciclo de vida ───────────────────────────────────────────
watch(() => item.value.idPrecoComercial, (novoId) => {
  isHeaderSaved.value = !!(novoId && novoId > 0)
  if (isHeaderSaved.value) {
    buscarEspecificacoes()
    buscarCampanhas()
  }
}, { immediate: true })

onMounted(async () => {
  await carregarListas()
})
</script>

<template>
  <FormularioCadastro
    module-name="Tabela de Preços"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    :ocultar-salvar="!isHeaderSaved"
    :desabilitar-salvar="isReadOnly"
    :desabilitar-salvar-alteracoes="isReadOnly"
    @submit="submitFormDoCabecalho"
    @submit-continuar="salvarAlteracoesCabecalho"
    @cancelar="handleCancel"
    @show-notification="(payload) => emit('show-notification', payload)"
  >
    <template #acoes-header v-if="isEditing">
      <button type="button" class="btn-icon-minimalista me-3" @click="mostrarAuditoria = true" title="Ver Histórico de Alterações">
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="!temPermissaoCompleta" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registro.</div>
    </div>

    <!-- Frame: Cabeçalho PrecoComercial -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary">
        <h5 class="mb-0"><i class="bi bi-tags me-2 text-primary"></i> Cabeçalho da Tabela de Preços</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-3">
            <CampoInput
              id="nome"
              label="Nome"
              v-model="item.nome"
              required
              maxlength="100"
              :readonly="isReadOnly"
            />
          </div>
          <div class="col-md-3">
            <CampoBuscaLista
              id="lstTipoPrecificacao"
              label="Tipo de Precificação"
              v-model="item.lstTipoPrecificacao"
              prefixo="TAB_PRECO_TP_PRECIFIC"
              contexto="Sistema"
              required
              :disabled="isReadOnly"
            />
          </div>
          <div class="col-md-2">
            <CampoInput
              id="valorBasePrecificacao"
              :label="tipoValorLabel(item.lstTipoPrecificacao)"
              :type="tipoValorInput(item.lstTipoPrecificacao)"
              v-model.number="item.valorBasePrecificacao"
              required
              :readonly="isReadOnly"
              :disabled="!item.lstTipoPrecificacao"
            />
          </div>
          <div class="col-md-2">
            <CampoBuscaLista
              id="lstMoeda"
              label="Moeda"
              v-model="item.lstMoeda"
              tabela="PrecoComercial"
              required
              :disabled="isReadOnly"
            />
          </div>
          <div class="col-md-2">
            <CampoBuscaLista
              id="lstStatus"
              label="Status"
              v-model="item.lstStatus"
              tabela="PrecoComercial"
              required
              :disabled="isReadOnly"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Botão Salvar Cabeçalho (apenas criação) -->
    <div class="mb-4 text-center" v-if="!isHeaderSaved && temPermissaoCompleta">
      <button
        type="button"
        class="btn btn-success px-4 py-2 fw-bold"
        @click="salvarCabecalhoLocalmente"
        :disabled="isSavingHeaderLocal"
      >
        <span v-if="isSavingHeaderLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-chevron-double-down me-1"></i>
        Salvar Cabeçalho e Liberar Abas
      </button>
      <div class="form-text mt-2 text-success">
        <i class="bi bi-info-circle me-1"></i> As abas de Especificações e Campanhas serão liberadas após salvar o cabeçalho.
      </div>
    </div>

    <!-- Abas -->
    <AbasFormulario
      v-model="abaAtiva"
      :abas="abas"
      cor="info"
      colapsavel
      :bloqueado="abasBloqueadas"
    >
      <!-- Aba: Especificações Produtos -->
      <template #tab-especificacoes>
        <div class="card bg-body-tertiary border-0 mb-3">
          <div class="card-body py-3">
            <h6 class="text-body-secondary text-uppercase small fw-bold mb-3">
              <i class="bi bi-funnel me-1"></i> Filtros
            </h6>
            <div class="row g-3">
              <div class="col-md-3">
                <CampoTimeStamp
                  id="filtroTsReferencia"
                  label="Vigentes em"
                  v-model="filtrosEspec.tsReferencia"
                  nivel="minuto"
                  :disabled="filtrosEspec.ignorarData"
                />
              </div>
              <div class="col-md-2">
                <CampoSelect
                  id="filtroIgnorarData"
                  label="Ignorar data"
                  v-model="filtrosEspec.ignorarData"
                  :options="simNaoOptions"
                  option-value="id"
                  option-label="label"
                />
              </div>
              <div class="col-md-4">
                <CampoBusca
                  id="filtroProdutoEspec"
                  label="Produto"
                  v-model="filtrosEspec.idProduto"
                  :configuracaoBusca="configBuscaProduto"
                />
              </div>
            </div>
            <div class="d-flex justify-content-end gap-2 mt-3">
              <button type="button" class="btn btn-secondary" @click="limparFiltrosEspec">Limpar</button>
              <button type="button" class="btn btn-success" @click="buscarEspecificacoes">
                <i class="bi bi-search me-1"></i> Buscar
              </button>
            </div>
          </div>
        </div>

        <!-- Toolbar da tabela -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="mb-0 text-body-secondary"><i class="bi bi-box-seam me-1"></i> Especificações</h6>
          <button type="button" class="btn btn-primary btn-sm" @click="abrirModalEspecificacao(null, null)" :disabled="isReadOnly">
            <i class="bi bi-plus-lg me-1"></i> Adicionar Especificação
          </button>
        </div>

        <div v-if="isLoadingEspec" class="text-center p-4"><div class="spinner-border text-info"></div></div>
        <div v-else class="table-responsive">
          <table class="table table-hover admin-table mb-0">
            <thead>
              <tr>
                <th>Produto</th>
                <th>SKU</th>
                <th>Descrição</th>
                <th class="text-center">Tipo</th>
                <th class="text-end">Valor Base</th>
                <th class="text-center">Início Vigência</th>
                <th class="text-center">Fim Vigência</th>
                <th class="text-center">Vigente</th>
                <th class="text-end" style="width: 100px;">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="e in especificacoes" :key="e.idPrecoComercialProduto">
                <td class="fw-medium">{{ e.nomeProduto || '-' }}</td>
                <td><code>{{ e.sku || '-' }}</code></td>
                <td class="text-body-secondary">{{ e.descricao || '-' }}</td>
                <td class="text-center">
                  <span class="badge bg-body-secondary text-body border border-secondary-subtle">
                    {{ labelTipoPrecificacao(e.lstTipoPrecificacao) }}
                  </span>
                </td>
                <td class="text-end fw-bold">{{ formatarValorBase(e.valorBasePrecificacao, e.lstTipoPrecificacao, e.lstMoeda) }}</td>
                <td class="text-center">{{ e.tsInicioVigencia ? formatarTimestampBR(e.tsInicioVigencia, 'minuto') : '-' }}</td>
                <td class="text-center">{{ e.tsFimVigencia ? formatarTimestampBR(e.tsFimVigencia, 'minuto') : '-' }}</td>
                <td class="text-center">
                  <span class="badge" :class="e.vigente ? 'badge-ghost-success' : 'badge-ghost-secondary'">
                    {{ e.vigente ? 'Vigente' : 'Fora' }}
                  </span>
                </td>
                <td class="text-end">
                  <div class="d-flex justify-content-end gap-1" v-if="!isReadOnly">
                    <button type="button" class="btn-icon" @click="abrirModalEspecificacao(e, null)" title="Editar">
                      <i class="bi bi-pencil-fill text-success"></i>
                    </button>
                    <button type="button" class="btn-icon" @click="excluirEspecificacao(e, false)" title="Excluir">
                      <i class="bi bi-trash-fill text-danger"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="especificacoes.length === 0">
                <td colspan="9" class="text-center py-4 text-muted">Nenhuma especificação encontrada.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Aba: Campanhas -->
      <template #tab-campanhas>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0 text-body-secondary"><i class="bi bi-megaphone me-1"></i> Campanhas da Tabela</h6>
          <button type="button" class="btn btn-primary btn-sm" @click="abrirModalCampanha(null)" :disabled="isReadOnly">
            <i class="bi bi-plus-lg me-1"></i> Nova Campanha
          </button>
        </div>

        <div v-if="isLoadingCampanhas" class="text-center p-4"><div class="spinner-border text-info"></div></div>
        <div v-else class="table-responsive mb-4">
          <table class="table table-hover admin-table mb-0">
            <thead>
              <tr>
                <th>Descrição</th>
                <th class="text-center">Tipo</th>
                <th class="text-end">Valor Base</th>
                <th class="text-center">Início</th>
                <th class="text-center">Fim</th>
                <th class="text-center">Vigente</th>
                <th class="text-center">Especificações</th>
                <th class="text-end" style="width: 100px;">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="c in campanhas"
                :key="c.idPrecoComercialCampanha"
                class="cursor-pointer"
                :class="{ 'linha-selecionada border-start border-4 border-info': campanhaSelecionada?.idPrecoComercialCampanha === c.idPrecoComercialCampanha }"
                @click="selecionarCampanha(c)"
              >
                <td class="fw-medium">{{ c.descricao }}</td>
                <td class="text-center">
                  <span class="badge bg-body-secondary text-body border border-secondary-subtle">
                    {{ labelTipoPrecificacao(c.lstTipoPrecificacao) }}
                  </span>
                </td>
                <td class="text-end fw-bold">{{ formatarValorBase(c.valorBasePrecificacao, c.lstTipoPrecificacao, item.lstMoeda) }}</td>
                <td class="text-center">{{ formatarTimestampBR(c.tsInicioVigencia, 'minuto') }}</td>
                <td class="text-center">{{ formatarTimestampBR(c.tsFimVigencia, 'minuto') }}</td>
                <td class="text-center">
                  <span class="badge" :class="c.vigente ? 'badge-ghost-success' : 'badge-ghost-secondary'">
                    {{ c.vigente ? 'Vigente' : 'Fora' }}
                  </span>
                </td>
                <td class="text-center">{{ c.qtdEspecificacoes }}</td>
                <td class="text-end">
                  <div class="d-flex justify-content-end gap-1" v-if="!isReadOnly">
                    <button type="button" class="btn-icon" @click.stop="abrirModalCampanha(c)" title="Editar">
                      <i class="bi bi-pencil-fill text-success"></i>
                    </button>
                    <button type="button" class="btn-icon" @click.stop="excluirCampanha(c)" title="Excluir">
                      <i class="bi bi-trash-fill text-danger"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="campanhas.length === 0">
                <td colspan="8" class="text-center py-4 text-muted">Nenhuma campanha cadastrada.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Especificações da campanha selecionada -->
        <div v-if="campanhaSelecionada" class="card border-info-subtle">
          <div class="card-header bg-info-subtle d-flex justify-content-between align-items-center">
            <h6 class="mb-0 text-info-emphasis">
              <i class="bi bi-box-seam me-1"></i> Especificações da Campanha: {{ campanhaSelecionada.descricao }}
            </h6>
            <button type="button" class="btn btn-primary btn-sm" @click="abrirModalEspecificacao(null, campanhaSelecionada.idPrecoComercialCampanha)" :disabled="isReadOnly">
              <i class="bi bi-plus-lg me-1"></i> Adicionar Especificação
            </button>
          </div>
          <div class="card-body">
            <div class="card bg-body-tertiary border-0 mb-3">
              <div class="card-body py-3">
                <h6 class="text-body-secondary text-uppercase small fw-bold mb-3">
                  <i class="bi bi-funnel me-1"></i> Filtros
                </h6>
                <div class="row g-3">
                  <div class="col-md-4">
                    <CampoBusca
                      id="filtroProdutoCampanha"
                      label="Produto"
                      v-model="filtroProdutoCampanha"
                      :configuracaoBusca="configBuscaProduto"
                    />
                  </div>
                </div>
                <div class="d-flex justify-content-end gap-2 mt-3">
                  <button type="button" class="btn btn-secondary" @click="filtroProdutoCampanha = null; buscarEspecificacoesCampanha()">Limpar</button>
                </div>
              </div>
            </div>

            <div v-if="isLoadingEspecCampanha" class="text-center p-4"><div class="spinner-border text-info"></div></div>
            <div v-else class="table-responsive">
              <table class="table table-hover admin-table mb-0">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>SKU</th>
                    <th class="text-center">Tipo</th>
                    <th class="text-end">Valor Base</th>
                    <th class="text-center">Início</th>
                    <th class="text-center">Fim</th>
                    <th class="text-end" style="width: 100px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="e in especificacoesCampanha" :key="e.idPrecoComercialProduto">
                    <td class="fw-medium">{{ e.nomeProduto || '-' }}</td>
                    <td><code>{{ e.sku || '-' }}</code></td>
                    <td class="text-center">
                      <span class="badge bg-body-secondary text-body border border-secondary-subtle">
                        {{ labelTipoPrecificacao(e.lstTipoPrecificacao) }}
                      </span>
                    </td>
                    <td class="text-end fw-bold">{{ formatarValorBase(e.valorBasePrecificacao, e.lstTipoPrecificacao, e.lstMoeda) }}</td>
                    <td class="text-center">{{ e.tsInicioVigencia ? formatarTimestampBR(e.tsInicioVigencia, 'minuto') : '-' }}</td>
                    <td class="text-center">{{ e.tsFimVigencia ? formatarTimestampBR(e.tsFimVigencia, 'minuto') : '-' }}</td>
                    <td class="text-end">
                      <div class="d-flex justify-content-end gap-1" v-if="!isReadOnly">
                        <button type="button" class="btn-icon" @click="abrirModalEspecificacao(e, campanhaSelecionada.idPrecoComercialCampanha)" title="Editar">
                          <i class="bi bi-pencil-fill text-success"></i>
                        </button>
                        <button type="button" class="btn-icon" @click="excluirEspecificacao(e, true)" title="Excluir">
                          <i class="bi bi-trash-fill text-danger"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="especificacoesCampanha.length === 0">
                    <td colspan="7" class="text-center py-4 text-muted">Nenhuma especificação nesta campanha.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </AbasFormulario>
  </FormularioCadastro>

  <!-- Modal: Campanha -->
  <ModalFormularioV2
    v-model="showModalCampanha"
    :titulo="campanhaEmEdicao?.idPrecoComercialCampanha ? 'Editar Campanha' : 'Nova Campanha'"
    cor-header="var(--bs-info)"
    :loading="isSavingCampanha"
    @confirmar="salvarCampanha"
  >
    <div class="row g-3" v-if="campanhaEmEdicao">
      <div class="col-md-8">
        <CampoInput
          id="campanhaDescricao"
          label="Descrição"
          v-model="campanhaEmEdicao.descricao"
          required
          maxlength="150"
          :disabled="isEditandoCampanha"
        />
      </div>
      <div class="col-md-4">
        <CampoBuscaLista
          id="campanhaLstTipoPrecificacao"
          label="Tipo de Precificação"
          v-model="campanhaEmEdicao.lstTipoPrecificacao"
          prefixo="TAB_PRECO_TP_PRECIFIC"
          contexto="Sistema"
          required
          :disabled="isEditandoCampanha"
          @selecionado="campanhaEmEdicao.valorBasePrecificacao = null"
        />
      </div>
      <div class="col-md-4">
        <CampoInput
          id="campanhaValorBasePrecificacao"
          :label="tipoValorLabel(campanhaEmEdicao.lstTipoPrecificacao)"
          :type="tipoValorInput(campanhaEmEdicao.lstTipoPrecificacao)"
          v-model.number="campanhaEmEdicao.valorBasePrecificacao"
          required
          :disabled="isEditandoCampanha || !campanhaEmEdicao.lstTipoPrecificacao"
        />
      </div>
      <div class="col-md-4">
        <CampoTimeStamp
          id="campanhaTsInicioVigencia"
          label="Início da Vigência"
          v-model="campanhaEmEdicao.tsInicioVigencia"
          nivel="minuto"
          required
        />
      </div>
      <div class="col-md-4">
        <CampoTimeStamp
          id="campanhaTsFimVigencia"
          label="Fim da Vigência"
          v-model="campanhaEmEdicao.tsFimVigencia"
          nivel="minuto"
          required
        />
      </div>
      <div v-if="isEditandoCampanha" class="col-12">
        <div class="alert alert-info border-0 py-2 mb-0">
          <i class="bi bi-lock-fill me-1"></i> Após a criação, apenas a vigência pode ser alterada. Descrição, tipo e valor base permanecem imutáveis.
        </div>
      </div>
    </div>
  </ModalFormularioV2>

  <!-- Modal: Especificação -->
  <ModalFormularioV2
    v-model="showModalEspecificacao"
    :titulo="especificacaoEmEdicao?.idPrecoComercialCampanha ? 'Especificação da Campanha' : 'Especificação de Produto'"
    cor-header="var(--bs-info)"
    :loading="isSavingEspec"
    @confirmar="salvarEspecificacao"
  >
    <div class="row g-3" v-if="especificacaoEmEdicao">
      <div class="col-md-8">
        <CampoBusca
          id="especIdProduto"
          label="Produto"
          v-model="especificacaoEmEdicao.idProduto"
          :configuracaoBusca="configBuscaProduto"
          required
        />
      </div>
      <div class="col-md-4">
        <CampoBuscaLista
          id="especLstTipoPrecificacao"
          label="Tipo de Precificação"
          v-model="especificacaoEmEdicao.lstTipoPrecificacao"
          prefixo="TAB_PRECO_TP_PRECIFIC"
          contexto="Sistema"
          required
          @selecionado="especificacaoEmEdicao.valorBasePrecificacao = null"
        />
      </div>
      <div class="col-md-4">
        <CampoInput
          id="especValorBasePrecificacao"
          :label="tipoValorLabel(especificacaoEmEdicao.lstTipoPrecificacao)"
          :type="tipoValorInput(especificacaoEmEdicao.lstTipoPrecificacao)"
          v-model.number="especificacaoEmEdicao.valorBasePrecificacao"
          required
          :disabled="!especificacaoEmEdicao.lstTipoPrecificacao"
        />
      </div>
      <div class="col-md-4">
        <CampoTimeStamp
          id="especTsInicioVigencia"
          label="Início da Vigência"
          v-model="especificacaoEmEdicao.tsInicioVigencia"
          nivel="minuto"
        />
      </div>
      <div class="col-md-4">
        <CampoTimeStamp
          id="especTsFimVigencia"
          label="Fim da Vigência"
          v-model="especificacaoEmEdicao.tsFimVigencia"
          nivel="minuto"
        />
      </div>
      <div class="col-12">
        <CampoInput
          id="especDescricao"
          label="Descrição"
          type="textarea"
          v-model="especificacaoEmEdicao.descricao"
          maxlength="200"
        />
      </div>
    </div>
  </ModalFormularioV2>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="PrecoComercial"
    :chave-primaria="item.idPrecoComercial"
    :titulo-registro="item.nome"
    contexto="tenant"
    :tabelas-filhas="['PrecoComercialCampanha', 'PrecoComercialProduto']"
    chave-estrangeira="IdPrecoComercial"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
.linha-selecionada {
  background-color: rgba(var(--bs-info-rgb), 0.08);
}
</style>
